import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../stores/authStore';
import { useBatteryStore } from '../stores/batteryStore';
import { useConfigStore } from '../stores/configStore';
import { sendDM } from '../services/dmService';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../lib/firebase/app';
import { colors } from '../styles/colors';
import { IconClose } from './icons';
import { DefaultAvatar } from './DefaultAvatar';

interface DMComposeModalProps {
  recipientId: string;
  recipientNickname: string;
  onClose: () => void;
  replyContent?: string;
}

interface SearchResult { uid: string; nickname: string; gender: 'male' | 'female' | 'unknown'; profileImageUrl?: string; }

export default function DMComposeModal({ recipientId: initId, recipientNickname: initNick, onClose, replyContent }: DMComposeModalProps) {
  const { t, i18n } = useTranslation();
  const isKo = i18n.language === 'ko';
  const user = useAuthStore((s) => s.user);
  const cells = useBatteryStore((s) => s.cells);
  const config = useConfigStore((s) => s.config);

  const [recipientId, setRecipientId] = useState(initId);
  const [recipientNickname, setRecipientNickname] = useState(initNick);
  const [content, setContent] = useState(replyContent ?? '');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  // User search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);

  const cost = config.dmSendCost;
  const canSend = content.trim().length > 0 && cells >= cost && !sending && recipientId;

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const kw = searchQuery.trim().toLowerCase();
      // Search all users and filter client-side
      const snap = await getDocs(query(collection(db, 'users')));
      const results: SearchResult[] = [];
      snap.forEach(doc => {
        const d = doc.data();
        if (doc.id === user?.uid) return;
        if (d.nickname?.toLowerCase().includes(kw)) {
          results.push({ uid: doc.id, nickname: d.nickname, gender: d.gender ?? 'unknown', profileImageUrl: d.profileImageUrl });
        }
      });
      setSearchResults(results.slice(0, 10));
    } catch (err) { console.error(err); }
    finally { setSearching(false); }
  };

  const handleSend = async () => {
    if (!canSend || !user || !recipientId) return;
    // Content filter check
    const { isContentClean, getFilterMessage } = await import('../services/contentFilter');
    if (!isContentClean(content.trim())) { alert(getFilterMessage(i18n.language)); return; }
    setSending(true);
    try {
      const ok = await useBatteryStore.getState().consume(user.uid, cost, 'DM_SEND');
      if (!ok) { alert(t('battery.low')); return; }
      await sendDM({ senderId: user.uid, senderNickname: user.nickname, senderGender: user.gender, senderProfileImageUrl: user.profileImageUrl, recipientId, recipientNickname, content: content.trim() });
      setSent(true);
    } catch (err) { console.error('[DM] Send failed:', err); }
    finally { setSending(false); }
  };

  const needRecipient = !recipientId;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60" />
      <div className="relative z-10 w-full max-w-sm overflow-hidden rounded-2xl"
        style={{ backgroundColor: colors.surface, border: `2px solid ${colors.border}` }}
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: `1px solid ${colors.border}` }}>
          <h3 className="text-sm font-bold" style={{ color: colors.text }}>{t('dm.compose')}</h3>
          <button onClick={onClose}><IconClose size={18} color={colors.textMuted} /></button>
        </div>

        {sent ? (
          <div className="flex flex-col items-center gap-3 px-4 py-8">
            <span className="text-3xl">✅</span>
            <p className="text-sm font-semibold" style={{ color: colors.text }}>{t('common.success')}</p>
            <button onClick={onClose} className="mt-2 rounded-lg px-6 py-2 text-sm font-bold text-white" style={{ backgroundColor: colors.primary }}>{t('common.confirm')}</button>
          </div>
        ) : (
          <div className="px-4 py-4">
            {/* Recipient */}
            {needRecipient ? (
              <div className="mb-3">
                <p className="mb-1 text-xs font-bold" style={{ color: colors.textMuted }}>{isKo ? '수신인 검색' : 'Find recipient'}</p>
                <div className="flex gap-1 mb-2">
                  <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={isKo ? '닉네임' : 'Nickname'}
                    className="flex-1 rounded-lg border bg-transparent px-2 py-1.5 text-sm outline-none"
                    style={{ borderColor: colors.border, color: colors.text }}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }} />
                  <button onClick={handleSearch} disabled={searching}
                    className="rounded-lg px-3 py-1.5 text-xs font-bold text-white" style={{ backgroundColor: colors.primary }}>
                    {searching ? '...' : t('common.search')}
                  </button>
                </div>
                {searchResults.length > 0 && (
                  <div className="max-h-40 overflow-y-auto rounded-lg" style={{ border: `1px solid ${colors.border}` }}>
                    {searchResults.map(r => {
                      const gc = r.gender === 'male' ? '#4a9eff' : r.gender === 'female' ? '#ff6b9d' : '#999';
                      return (
                        <button key={r.uid} onClick={() => { setRecipientId(r.uid); setRecipientNickname(r.nickname); setSearchResults([]); }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-white/5" style={{ borderBottom: `1px solid ${colors.border}` }}>
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full" style={{ border: `2px solid ${gc}`, backgroundColor: colors.card }}>
                            {r.profileImageUrl ? <img src={r.profileImageUrl} alt="" className="h-full w-full object-cover"/> : <DefaultAvatar gender={r.gender} size={18}/>}
                          </div>
                          <div>
                            <p className="text-sm font-bold" style={{ color: colors.text }}>{r.nickname}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <div className="mb-3 flex items-center gap-2">
                <span className="text-xs" style={{ color: colors.textMuted }}>To:</span>
                <span className="rounded-full px-2.5 py-0.5 text-xs font-bold" style={{ backgroundColor: colors.card, color: colors.text }}>{recipientNickname}</span>
                {initId === '' && <button onClick={() => { setRecipientId(''); setRecipientNickname(''); }} className="text-[10px]" style={{ color: colors.textMuted }}>✕</button>}
              </div>
            )}

            <textarea value={content} onChange={(e) => setContent(e.target.value)}
              placeholder={t('dm.placeholder')}
              className="w-full rounded-lg border bg-transparent px-3 py-2.5 text-sm outline-none"
              style={{ borderColor: colors.border, color: colors.text, minHeight: 100 }} autoFocus={!!recipientId} />

            <p className="mt-2 text-[11px]" style={{ color: cells >= cost ? colors.textMuted : colors.error }}>
              {t('dm.sendCost', { cost: cost.toLocaleString() })}
              {cells < cost && ` (${t('battery.low')})`}
            </p>

            <div className="mt-3 flex gap-2">
              <button onClick={onClose} className="flex-1 rounded-lg py-2.5 text-sm font-semibold"
                style={{ backgroundColor: colors.card, color: colors.textMuted }}>{t('common.cancel')}</button>
              <button onClick={handleSend} disabled={!canSend}
                className="flex-1 rounded-lg py-2.5 text-sm font-bold text-white disabled:opacity-50"
                style={{ backgroundColor: colors.primary }}>{sending ? '...' : t('common.send')}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
